import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import { MillingBatch, MillingBatchStatus, MillingJob, MillingJobCopyReason, MillingJobStatus } from 'models/milling';
import DownloadIcon from '@mui/icons-material/Download';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useMutation } from '@apollo/client';
import {
    deleteMillingBatchTemplate,
    finishMillingBatchTemplate,
    redoMillingJobTemplate,
} from 'apollo/templates/milling';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import StlMultiPreviewList from 'components/shared/stl-multi-preview-list/stl-multi-preview-list.component';
import ChangeWorkflowStepDialog, {
    OrderStepInput,
} from 'components/orders/shared/change-workflow-step-dialog.component';
import WorkflowActionsDialog from 'components/shared/workflow-actions/workflow-actions-dialog.component';
import { OrderStep } from 'models/workflow-step';
import EditIcon from '@mui/icons-material/Edit';
import { changeWorkflowStepForManyTemplate } from 'apollo/templates/workFlow';
import { Order } from 'models/order';
import { LoadingButton } from '@mui/lab';

type MillingBatchDialogProps = {
    batch?: MillingBatch;
    open: boolean;
    onClose?: () => void;
    onBatchChanged?: () => void;
};
const MillingBatchDialog: FC<MillingBatchDialogProps> = ({
    batch,
    open,
    onClose,
    onBatchChanged,
}: MillingBatchDialogProps) => {
    const { t: translate } = useTranslation(['common', 'milling-jobs', 'orders', 'workflow']);
    const [deleteBatchConfirmDialogOpen, setDeleteBatchConfirmDialogOpen] = useState(false);
    const [redoConfirmDialogOpen, setRedoConfirmDialogOpen] = useState(false);
    const [redoJobId, setRedoJobId] = useState('');
    const [stepDialogOpen, setStepDialogOpen] = useState(false);
    const [currentStepOrderId, setCurrentStepOrderId] = useState<string>();
    const [jobOrderStepMap, setJobOrderStepMap] = useState<Map<string, OrderStepMapItem>>(new Map());
    const [changeStepForAllOpen, setChangeStepForAllOpen] = useState(false);
    const [changeStepLoading, setChangeStepLoading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        if (batch) {
            const newJobOrderStepMap = new Map<string, OrderStepMapItem>();
            batch.millingJobs.forEach((job) => {
                newJobOrderStepMap.set(job._id as string, {
                    orderId: job.order._id,
                    step: job.order.workFlow?.currentStep,
                });
            });
            setJobOrderStepMap(newJobOrderStepMap);
        }
    }, [batch]);

    const [finishMillingBatch] = useMutation(finishMillingBatchTemplate, {
        onCompleted: () => {
            if (onBatchChanged && onClose) {
                onBatchChanged();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMillingBatch] = useMutation(deleteMillingBatchTemplate, {
        onCompleted: () => {
            if (onBatchChanged && onClose) {
                onBatchChanged();
                onClose();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [redoMillingJob] = useMutation(redoMillingJobTemplate, {
        onCompleted: () => {
            if (onBatchChanged) {
                onBatchChanged();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [changeWorkflowStepForMany] = useMutation(changeWorkflowStepForManyTemplate, {
        onCompleted: ({ changeWorkflowStepForMany }) => {
            setChangeStepLoading(false);
            if (onBatchChanged) {
                onBatchChanged();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFinishClick = () => {
        if (batch) {
            finishMillingBatch({ variables: { millingBatchId: batch._id } });
        }
    };

    const handleConfirmDeleteBatch = () => {
        setDeleteBatchConfirmDialogOpen(false);
        if (batch) {
            deleteMillingBatch({ variables: { millingBatchId: batch._id } });
        }
    };

    const handleRedoClick = (jobId: string) => {
        setRedoConfirmDialogOpen(true);
        setRedoJobId(jobId);
    };

    const handleConfirmRedoJob = () => {
        setRedoConfirmDialogOpen(false);
        if (redoJobId) {
            redoMillingJob({ variables: { millingJobId: redoJobId, reason: MillingJobCopyReason.REDO } });
        }
    };

    const deleteDisabled = () => {
        return !!batch?.millingJobs.find((job) => job.status === MillingJobStatus.REDONE);
    };

    const changeStepForJob = (job: MillingJob) => {
        if (job.order) {
            setCurrentStepOrderId(job.order._id);
            setStepDialogOpen(true);
        }
    };

    const handleStepDialogClose = () => {
        setStepDialogOpen(false);
        setChangeStepForAllOpen(false);
        setCurrentStepOrderId(undefined);
    };

    const handleOrderStepChange = (orderId: string | undefined, step: OrderStepInput) => {
        if (batch && orderId) {
            const newJobOrderStepMap = new Map<string, OrderStepMapItem>();
            jobOrderStepMap.forEach((mapItem, jobId) => {
                let newMapItem = mapItem;
                if (mapItem && newMapItem.orderId.toString() === orderId.toString()) {
                    newMapItem = { orderId: orderId, step: { ...newMapItem.step, ...step } };
                }
                newJobOrderStepMap.set(jobId, newMapItem);
            });
            setJobOrderStepMap(newJobOrderStepMap);
        }
    };

    const handleAllOrdersStepChange = async (
        _orderId: string | undefined,
        step: OrderStepInput,
        deadline: Date | null,
    ) => {
        setChangeStepLoading(true);
        await changeWorkflowStepForMany({
            variables: { orderIds: batch?.millingJobs.map((job) => job.order._id), orderCurrentStep: step },
        });
    };

    return (
        <Fragment>
            <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
                <DialogTitle>
                    <Box display="flex">
                        <Box flexGrow={1}>
                            <Box>{translate('milling-jobs:MILLING_BATCH')}</Box>
                            <Box>
                                <Typography variant="caption">
                                    {translate('milling-jobs:PRODUCED_BY')} {batch?.createdBy.fullName} (
                                    {formatDate(batch?.createdAt || new Date(), 'datetime')})
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            {batch?.status === MillingBatchStatus.IN_PRODUCTION ? (
                                <Chip variant="outlined" label={translate('milling-jobs:IN_PRODUCTION')} color="info" />
                            ) : (
                                <Chip label={translate('milling-jobs:FINISHED')} color="success" />
                            )}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box mt={1} mb={3}>
                        <Button
                            href={batch?.zipFileKey}
                            disabled={!batch?.zipFileKey}
                            startIcon={<DownloadIcon />}
                            sx={{ marginRight: 1 }}
                        >
                            {translate('common:DOWNLOAD_ALL_FILES')}
                        </Button>
                        <LoadingButton onClick={() => setChangeStepForAllOpen(true)} loading={changeStepLoading}>
                            {translate('milling-jobs:CHANGE_STEP_FOR_ALL_ORDERS')}
                        </LoadingButton>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('milling-jobs:JOB_GROUP')}</TableCell>
                                    <TableCell>{translate('common:FILENAME')}</TableCell>
                                    <TableCell>{translate('orders:COMMENT')}</TableCell>
                                    <TableCell>{translate('orders:ORDER')}</TableCell>
                                    <TableCell>{translate('orders:INTERNAL_ORDER_ID')}</TableCell>
                                    <TableCell>{translate('workflow:STEP')}</TableCell>
                                    <TableCell sx={{ width: 280 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {batch?.millingJobs.map((job) => (
                                    <TableRow key={job._id}>
                                        <TableCell>
                                            {job.status === MillingJobStatus.REDONE && (
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="warning.main"
                                                    mr={1}
                                                >
                                                    {translate('milling-jobs:REDONE')}
                                                </Typography>
                                            )}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                sx={{
                                                    textDecoration:
                                                        job.status === MillingJobStatus.REDONE
                                                            ? 'line-through'
                                                            : 'none',
                                                }}
                                            >
                                                {job.jobType}: {job.material} {job.color}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{job.filename}</TableCell>
                                        {job.comment ? (
                                            <Tooltip title={job.comment} placement="top">
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: 200,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {job.comment}
                                                    </Typography>
                                                </TableCell>
                                            </Tooltip>
                                        ) : (
                                            <TableCell>-</TableCell>
                                        )}
                                        <TableCell>
                                            <Link
                                                style={{ cursor: 'pointer' }}
                                                target="_blank"
                                                href={`/order-details/${job.order._id}/main-view`}
                                            >
                                                {job.order.orderId}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            {job.order.internalOrderId
                                                ? job.order.internalOrderId
                                                : translate('orders:NO_INTERNAL_ORDER_ID')}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <IconButton size="small" onClick={() => changeStepForJob(job)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <Box>
                                                    {jobOrderStepMap.get(job._id as string)?.step?.name ||
                                                        translate('workflow:NO_STEP')}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            {job.status !== MillingJobStatus.REDONE && (
                                                <Button
                                                    color="error"
                                                    startIcon={<UndoIcon />}
                                                    onClick={() => handleRedoClick(job._id as string)}
                                                >
                                                    {translate('milling-jobs:REDO_JOB')}
                                                </Button>
                                            )}

                                            {job.stlFiles?.length > 0 && (
                                                <StlMultiPreviewList stlFiles={job.stlFiles} />
                                            )}
                                            <IconButton
                                                color="primary"
                                                href={job.zipFileKey}
                                                disabled={!job.zipFileKey}
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Box display="flex" alignItems="center" flexGrow={1}>
                        <Box flexGrow={1}>
                            <Button
                                disabled={deleteDisabled()}
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteBatchConfirmDialogOpen(true)}
                            >
                                {translate('milling-jobs:DELETE_MILLING_BATCH')}
                            </Button>
                        </Box>
                        <Box>
                            <Button onClick={onClose}>{translate('common:CLOSE')}</Button>
                        </Box>
                        {batch?.status === MillingBatchStatus.IN_PRODUCTION && (
                            <Box ml={1}>
                                <Button variant="contained" startIcon={<CheckIcon />} onClick={handleFinishClick}>
                                    {translate('milling-jobs:FINISH_PRODUCTION')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={deleteBatchConfirmDialogOpen || false}
                handleClose={() => setDeleteBatchConfirmDialogOpen(false)}
                dialogTitle={translate('milling-jobs:DELETE_BATCH')}
                dialogContent={translate('milling-jobs:CONFIRM_DELETE_BATCH')}
                handleConfirm={handleConfirmDeleteBatch}
            />
            <ConfirmDialog
                open={redoConfirmDialogOpen || false}
                handleClose={() => setRedoConfirmDialogOpen(false)}
                dialogTitle={translate('milling-jobs:REDO_JOB')}
                dialogContent={translate('milling-jobs:CONFIRM_REDO_JOB')}
                handleConfirm={handleConfirmRedoJob}
            />

            {/* Change step one order */}
            <WorkflowActionsDialog
                open={stepDialogOpen}
                onClose={handleStepDialogClose}
                orderId={currentStepOrderId}
                onStepChanged={() => onClose?.()}
            />

            {/* Change step for all orders */}
            <ChangeWorkflowStepDialog
                open={changeStepForAllOpen}
                onClose={handleStepDialogClose}
                onUpdateStep={handleAllOrdersStepChange}
                performMutation={false}
            />
        </Fragment>
    );
};

type OrderStepMapItem = { orderId: string; step: OrderStep };

export default MillingBatchDialog;
